<template>
  <el-form
    v-if="feed"
    :model="feed"
    label-position="top"
    :class="['feed-form', { desktop: isDesktop }]"
    @submit.prevent
  >
    <el-form-item :error="errors.name" label="Name">
      <el-input v-model="feed.name" type="text" />
    </el-form-item>
    <el-form-item label="Stream">
      <StreamSelector v-model="feed.stream" :disabled="disabled" />
    </el-form-item>
    <el-form-item label="Feed type">
      <el-switch
        v-model="feed.open_rtb"
        :disabled="disabled"
        active-text="Open RTB endpoint"
        inactive-text="XML/JSON feed"
      />
    </el-form-item>
    <el-form-item label="Ad type">
      <AdTypeSelect
        v-model="feed.type_ids"
        :disabled="disabled"
        :radio-view="true"
        :allowed-types="allowedTypes"
      />
    </el-form-item>
    <el-form-item
      v-if="showBannerSizes"
      label="Banner sizes"
      :error="errors.size_ids"
    >
      <BannerSizesSelector
        v-model="feed.size_ids"
        :disabled="disabled"
        @update:modelValue="update"
      />
    </el-form-item>
    <template v-if="feed.open_rtb">
      <el-form-item
        class="wide-field"
        label="Endpoint"
        :error="errors.feed_format_id"
        :rules="{ required: true, message: 'Wrong link', trigger: 'blur' }"
      >
        <el-input
          v-model="feed.url"
          type="text"
          :disabled="disabled"
          @update:modelValue="update"
        />
      </el-form-item>
      <div class="rtb-info">
        <el-link
          href="https://www.iab.com/wp-content/uploads/2016/03/OpenRTB-API-Specification-Version-2-5-FINAL.pdf"
          target="_blank"
          type="primary"
        >
          Open RTB 2.5 IAB specification
        </el-link>
        <el-link
          v-if="feed.type_ids && RtbAdTypeLinkInfo[feed.type_ids as DspAdType]"
          :href="RtbAdTypeLinkInfo[feed.type_ids as DspAdType]"
          target="_blank"
          type="primary"
        >
          Please check integration tech document
        </el-link>
      </div>
    </template>
    <template v-else>
      <el-form-item
        class="wide-field"
        label="Feed"
        :error="errors.url"
        :rules="{ required: true, message: 'Wrong link', trigger: 'blur' }"
      >
        <el-input v-model="feed.url" :disabled="disabled" type="text" />
      </el-form-item>
      <FeedMacrosInfo />
      <el-form-item
        v-if="isStaff"
        :error="errors.feed_format_id"
        label="Integration format"
        label-width="150px"
      >
        <FeedFormatSelector
          v-model="feed.feed_format_id"
          :disabled="disabled"
          @update:modelValue="update"
        />
      </el-form-item>
    </template>
    <RefreshRateInput
      v-model="feed.refresh_rate"
      entity="feed"
      @update:modelValue="update"
    />
    <FeedTargeting v-model="feed" @update="update" />
    <FeedFilters v-model="feed" @update="update" />
    <FeedRecommendedPrices v-if="feed.open_rtb" :feed="feed" />
    <el-form-item>
      <el-button
        type="primary"
        plain
        :disabled="loading"
        :icon="loading ? 'LoadingElementIcon' : ''"
        @click.prevent="onSubmit(false)"
      >
        {{ actionText }}
      </el-button>
      <el-button
        v-if="!feed.id"
        type="primary"
        plain
        :disabled="loading"
        :icon="loading ? 'LoadingElementIcon' : ''"
        @click.prevent="onSubmit(true)"
      >
        Create and send to review
      </el-button>
      <router-link to="/feeds" class="el-button is-plain">
        <span>Cancel</span>
      </router-link>
    </el-form-item>
  </el-form>
</template>

<script lang="ts">
import { defineComponent, computed, ref } from 'vue'
import AdTypeSelect from '@/components/Select/AdTypeSelect.vue'
import StreamSelector from '@/apps/traforama/components/StreamSelector.vue'

import FeedFormatSelector from './FeedFormatSelector.vue'
import FeedTargeting from './FeedTargeting.vue'
import FeedFilters from './FeedFilters.vue'
import FeedMacrosInfo from './FeedMacrosInfo.vue'
import FeedRecommendedPrices from './FeedRecommendedPrices.vue'
import RefreshRateInput from '@/apps/traforama/components/RefreshRateInput.vue'
import BannerSizesSelector from '@/components/Select/BannerSizesSelector.vue'

import {
  FeedValidationErrorsKey,
  AdType,
  DspAdType,
  RonType
} from '@/api/types'
import { useUserStore } from '@/store/modules/user'
import useValidation from '@/hooks/useValidation'
import useResize from '@/hooks/useResize'

const RtbAdTypeLinkInfo: { [title in DspAdType]: string } = {
  [AdType.Popunder]:
    'https://support.traforama.com/en/articles/popunder-open-rtb-integration',
  [AdType.Interstitial]:
    'https://support.traforama.com/en/articles/popunder-open-rtb-integration',
  [AdType.Banner]:
    'https://support.traforama.com/en/articles/banner-open-rtb-integration',
  [AdType.Overlay]:
    'https://support.traforama.com/en/articles/video-preroll-slider-open-rtb-integration',
  [AdType.InpagePush]:
    'https://support.traforama.com/en/articles/in-page-push-open-rtb-integration',
  [AdType.Slider]:
    'https://support.traforama.com/en/articles/video-preroll-slider-open-rtb-integration',
  [AdType.OutStream]:
    'https://support.traforama.com/en/articles/video-preroll-slider-open-rtb-integration'
}

export default defineComponent({
  name: 'FeedForm',
  components: {
    AdTypeSelect,
    FeedFormatSelector,
    FeedFilters,
    FeedTargeting,
    FeedMacrosInfo,
    FeedRecommendedPrices,
    StreamSelector,
    RefreshRateInput,
    BannerSizesSelector
  },
  props: {
    modelValue: {
      required: true,
      type: Object
    },
    loading: {
      type: Boolean,
      default: false
    },
    actionText: {
      type: String,
      default: 'Save'
    }
  },
  setup(props, context) {
    const userStore = useUserStore()

    const { isStaff } = userStore
    const { validationErrors } = useValidation(FeedValidationErrorsKey)
    const errors = validationErrors()
    const feed = ref(props.modelValue ? props.modelValue : {})

    const disabled = computed(() => {
      return userStore.staff ? false : feed.value.id !== undefined
    })

    const allowedTypes = computed(() => {
      let types = [AdType.Popunder, AdType.Interstitial, AdType.InpagePush]
      if (feed.value && feed.value.open_rtb) {
        types = [
          ...types,
          AdType.Overlay,
          AdType.Slider,
          AdType.OutStream,
          AdType.Banner
        ]
      }
      return types
    })

    const showBannerSizes = computed(() => {
      return feed.value.type_ids === AdType.Banner
    })

    const { isDesktop } = useResize()

    const onSubmit = function (withReview?: boolean) {
      context.emit('submit', { feed: feed.value, withReview: withReview })
    }

    const update = async (val: any) => {
      feed.value = { ...feed.value, ...val }
      await context.emit('update:modelValue', feed.value)
    }

    return {
      onSubmit,
      disabled,
      errors,
      isDesktop,
      AdType,
      feed,
      update,
      allowedTypes,
      showBannerSizes,
      isStaff,
      RtbAdTypeLinkInfo
    }
  }
})
</script>

<style lang="scss" scoped>
.feed-form {
  :deep(.el-form-item) {
    margin-bottom: 10px;

    .el-form-item__label {
      padding: 0;
    }
  }

  :deep(.el-collapse) {
    width: 100%;
  }

  &.desktop {
    > :deep(.el-form-item) {
      > .el-form-item__content {
        > .el-input,
        > .el-select {
          width: 33%;
          max-width: 33%;
        }
      }
    }

    :deep(.third-width-control .el-select) {
      width: 33%;
      max-width: 33%;
    }

    :deep(.wide-field .el-form-item__content > .el-input) {
      width: 66%;
      max-width: 66%;
    }
  }
}

.rtb-info {
  display: flex;
  justify-content: space-between;
}
</style>
